import React, { useState, useEffect, useRef } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button"; 
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import { doc, setDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { nanoid } from "nanoid";

import { db, storage, auth } from "../firebaseConfig";
import LoadingButton from "../Commons/LoadingButton";
import SnackbarMsg from "../Commons/snackbarMsg";
import { BOOKING_CHAT_TYPE_SYSTEM, CURRENCY_RATES, DUMMY_SUPP_ID } from "../Constants.js";
import { devConsolelog } from "../Utility.js";

const BookingForm = ({ pkg, selectedCabIndex, handleClose, handleSubmit }) => {
  const selectedCab = pkg.cabPricing[selectedCabIndex];
  const userData = JSON.parse(localStorage.getItem("user"));
  const conversionRate = CURRENCY_RATES[pkg.currency] || 80;
  const priceInINR = pkg.currency === "INR" ? selectedCab.price : selectedCab.price * conversionRate;
  const tokenAmount = Math.ceil(priceInINR * 0.3);

  const [formErrors, setFormErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showAlertData, setAlertOpen] = useState({ show: false });
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState("");
  // const [bookSuccessPopup, setBookSuccessPopup] = useState(false);
  const [formData, setFormData] = useState({
    leadPaxName: "",
    leadPaxContact: "",
    adults: "",
    children: "",
    startDate: "",
    tokenAmount: tokenAmount,
    pickUp: "",
    arrivalDetails: "",
  });
  devConsolelog("pkg id ", pkg.id, pkg._id);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
      setFileName(e.target.files[0].name);
    }
  };

  const validate = () => {
    let errors = {};
    if (!formData.leadPaxName) errors.leadPaxName = true;
    if (!formData.leadPaxContact) errors.leadPaxContact = true;
    if (!formData.adults) errors.adults = true;
    if (!formData.startDate) errors.startDate = true;
    if (!file) errors.screenshot = true;
    if (!formData.arrivalDetails) errors.arrivalDetails = true;
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleBookingSubmit = async () => {
    if (!validate()) return;

    setLoading(true);
    try {
      const user = auth.currentUser;
      if (!user) {
        setAlertOpen({ show: true, message: "User not authenticated", severity: "error" });
        setLoading(false);
        return;
      }

      // 1. Upload Screenshot
      let screenshotUrl = "";
      if (file) {
        const storageRef = ref(storage, `payment-docs/${nanoid(5)}_${"cab-pkg"}_token_sc_${file.name}`);
        const snapshot = await uploadBytes(storageRef, file);
        screenshotUrl = await getDownloadURL(snapshot.ref);
      }

      // 2. Create Booking Document
      const bookingId = `B-${nanoid()}`;
      const bookingData = {
        bookingId,
        acceptedBid: priceInINR,
        //bidDetails: acceptedBid,
        status: "initiated",  //"active",
        tokenPayment: {
          amount: formData?.tokenAmount,
          currency: 'inr',
          paymentScUrl: screenshotUrl,
          paymentDate: Date.now(),
          paymentVerified: false
        },
        //supplierId: acceptedBid.supplierId,
        cabDetailsShared: false,
        driverDetailsShared: false,
        chats: [
          {
            id: nanoid(5),
            sender: BOOKING_CHAT_TYPE_SYSTEM,
            text: "CabEasy: Please chat directly with your booking partner here and share all the details necessary. *Any post booking dispute will be resolved only referring to data updated in this chat!",
            type: "text",
            createdAt: Date.now(),
          },
          {
            id: nanoid(5),
            sender: BOOKING_CHAT_TYPE_SYSTEM,
            text: `Token Amount paid to CabEasy`, //: INR ${tokenAmt}
            type: "text",
            createdAt: Date.now(),
          }
        ],
        supplierId: DUMMY_SUPP_ID,
        req: {
          reqId: pkg?.id || `R-${nanoid()}`,
          destination: pkg?.destination,
          agentId: userData.phone,
          startDate: formData?.startDate,
          queryType: "cab-pkg",
          otherInfo: pkg?.detailedItinerary || pkg?.itinerary,
          noOfNights: pkg?.destination,
          pax: Number(formData?.adults) + Number(formData?.children),
          adults: Number(formData?.adults),
          children: Number(formData?.children),
          pickUp: formData?.pickUp,
          trackingId: formData?.leadPaxName,
          cabType: selectedCab?.cabType,

        },
        createdAt: Date.now(),
      };
      
      // {
      //   bookingId,
      //   pkgId: pkg.id || "unknown", // Assuming pkg has an id, otherwise we might need to pass it
      //   pkgTitle: pkg.title,
      //   selectedCab: selectedCab,
      //   ...formData,
      //   totalPax: parseInt(formData.adults || 0) + parseInt(formData.children || 0),
      //   screenshotUrl,
      //   status: "pending_verification",
      //   agentId: user.uid,
      //   createdAt: Date.now(),
      // };

      console.log("booking data ", bookingData);
      await setDoc(doc(db, "bookings", bookingId), bookingData);

      handleSubmit({"bookingId": bookingId});
      setTimeout(() => {
        handleClose();
      }, 1000);
    } catch (error) {
      console.error("Booking error:", error);
      setAlertOpen({ show: true, message: "Failed to submit booking. Please try again.", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 1 }}>
      <SnackbarMsg open={showAlertData.show} message={showAlertData.message} onClose={() => setAlertOpen({ show: false })} />

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Lead Pax Name*"
            name="leadPaxName"
            size="small"
            value={formData.leadPaxName}
            onChange={handleInputChange}
            error={!!formErrors.leadPaxName}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Lead Pax Contact*"
            name="leadPaxContact"
            size="small"
            value={formData.leadPaxContact}
            onChange={handleInputChange}
            error={!!formErrors.leadPaxContact}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Adults*"
            name="adults"
            type="number"
            size="small"
            value={formData.adults}
            onChange={handleInputChange}
            error={!!formErrors.adults}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Children"
            name="children"
            type="number"
            size="small"
            value={formData.children}
            onChange={handleInputChange}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Trip Start Date*"
            name="startDate"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={formData.startDate}
            onChange={(e) => {
              let formattedVal = Date.parse(new Date(e.target?.value));
              setFormData((prev) => ({ ...prev, [e.target.name]: formattedVal }));
            }}
            error={!!formErrors.startDate}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Pickup location*"
            name="pickUp"
            size="small"
            value={formData.pickUp}
            onChange={handleInputChange}
            error={!!formErrors.pickUp}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Token Advance Amount (INR)*"
            name="tokenAmount"
            type="number"
            size="small"
            value={formData.tokenAmount}
            onChange={handleInputChange}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            Suggested 30% advance: {tokenAmount} INR
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Button
            variant="outlined"
            component="label"
            fullWidth
            size="small"
            sx={{ height: '40px' }}
            color={formErrors.screenshot ? "error" : "primary"}
          >
            {fileName || "Upload Token Screenshot*"}
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={handleFileChange}
            />
          </Button>
          {fileName && (
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
              Selected: {fileName}
            </Typography>
          )}
        </Grid>
        
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Arrival/Pickup Details*"
            name="arrivalDetails"
            multiline
            rows={2}
            size="small"
            value={formData.arrivalDetails}
            onChange={handleInputChange}
            error={!!formErrors.arrivalDetails}
          />
        </Grid>
      </Grid>

      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <LoadingButton
          variant="contained"
          sx={{ mt: 3, px: 4 }}
          loading={loading}
          onClick={handleBookingSubmit}
        >
          Confirm Booking
        </LoadingButton>
      </Box>

      {/* <PopUp
        open={bookSuccessPopup}
        checkMark={false}
        forwardRef={`/booking/${newBookingId.current}`}
        primaryText="Congratulations!"
        secondaryText="A booking is created for your requirement"
        secondaryText1="Please share Client & arrival details in the booking chat. Separate Button for Client contact number."
        secondaryText2="Driver details will be shared 1 day before pickup"
        secondaryText3="You will now be redirected to your booking details."
        submitText={"Chat with Supplier"}
      /> */}
    </Box>
  );
};

export default BookingForm;
